import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import arrowLeftSvg from '@/assets/icons/chevron-left.svg'
import arrowRightSvg from '@/assets/icons/chevron-right.svg'
import ChevronUpSvg from '@/assets/icons/chevron-up.svg?react'
import type {FullProduct} from '@/models/productModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useIsChanged from '@/utils/hooks/useIsChanged'
import preloadImage from '@/utils/preloadImage'
import style from '@/views/Product/Product.module.scss'

const SLIDE_COUNT = 5

export default memo(function ProductVisual({
  images,
  title,
  ask,
}: {
  images: FullProduct['images']
  title: FullProduct['title']
  ask: (q: string) => void
}) {
  const [imagePos, setImagePos] = useState(0)

  const isImageNextable = imagePos < images.length - 1
  const isImagePrevable = imagePos > 0

  const nextImage = useCallback(() => {
    setImagePos(pos => {
      if (pos < images.length - 1) return pos + 1
      return pos
    })
  }, [images])

  const prevImage = useCallback(() => {
    setImagePos(pos => {
      if (pos > 0) return pos - 1
      return pos
    })
  }, [])

  useEffect(() => {
    preloadImage(images)
  }, [images])

  //----------------------------------------------------------------------------

  const [slidePos, setSlidePos] = useState(0)

  const isSlideNextable = slidePos < images.length - SLIDE_COUNT
  const isSlidePrevable = slidePos > 0

  const slidePrevious = useCallback(() => {
    setSlidePos(pos => {
      if (pos > 0) return pos - 1
      return pos
    })
  }, [])

  const slideNext = useCallback(() => {
    setSlidePos(pos => {
      if (images.length - SLIDE_COUNT > pos) return pos + 1
      return pos
    })
  }, [images.length])

  //----------------------------------------------------------------------------

  // Reset slidePos and imagePos when images.length changes
  const imagesLengthChanged = useIsChanged(images.length)

  if (imagesLengthChanged) {
    if (images.length - 1 < imagePos) setImagePos(images.length - 1)

    if (images.length - SLIDE_COUNT < slidePos) {
      setSlidePos(Math.max(images.length - SLIDE_COUNT, 0))
    }
  }

  //----------------------------------------------------------------------------

  const handleDescribeImage = useCallback(
    (index: number) => {
      const number = (() => {
        const numberStr = (index + 1).toString()
        if (numberStr.endsWith('1')) return `${numberStr}st`
        if (numberStr.endsWith('2')) return `${numberStr}nd`
        if (numberStr.endsWith('3')) return `${numberStr}rd`
        return `${numberStr}th`
      })()

      ask(`Describe the ${number} image of the product`)
    },
    [ask],
  )

  return (
    <div
      className={style.ProductVisual}
      role='region'
      aria-roledescription='carousel'
      aria-label={`${title}'s images`}
    >
      {images.length > 1 && (
        <div className={style.ProductImages} role='region' aria-label='Images controls'>
          <button
            className={clsx(style.Arrow, style.ArrowUp, !isSlidePrevable && style.IsHidden)}
            onClick={slidePrevious}
            aria-disabled={!isSlidePrevable}
            aria-label='Previous preview thumbnail'
          >
            <ChevronUpSvg width={24} height={24} aria-hidden />
          </button>
          <div className={style.ProductImageSlide}>
            {images.slice(slidePos, slidePos + SLIDE_COUNT).map((image, idx) => (
              <Fragment key={image}>
                <button
                  onClick={() => {
                    setImagePos(idx + slidePos)
                  }}
                  aria-label={`Show image ${idx + slidePos + 1} of ${images.length}`}
                >
                  <img src={image} alt='' />
                </button>
                <VisuallyHidden
                  strict
                  as='button'
                  onClick={() => {
                    handleDescribeImage(idx)
                  }}
                  aria-label={`Ask Innosearch to describe image ${idx + slidePos + 1} of ${images.length}`}
                >
                  {`Ask about image ${idx + slidePos + 1} of ${images.length} from Innosearch and get a description`}
                </VisuallyHidden>
              </Fragment>
            ))}
          </div>
          <button
            className={clsx(style.Arrow, style.ArrowDown, !isSlideNextable && style.IsHidden)}
            aria-disabled={!isSlideNextable}
            onClick={slideNext}
            aria-label='Next preview thumbnail'
          >
            <ChevronDownSvg width={24} height={24} aria-hidden />
          </button>
        </div>
      )}
      <div className={style.CurrentImage} role='region' aria-label='Current image'>
        <VisuallyHidden
          strict
          as='button'
          isVisible={isImagePrevable}
          className={clsx(style.Arrow, style.ArrowPrev)}
          onClick={prevImage}
          aria-label='Previous image'
          aria-disabled={!isImagePrevable}
        >
          <img src={arrowLeftSvg} alt='' />
        </VisuallyHidden>
        <img className={style.ProductImage} src={images[imagePos]} alt={title} />
        <VisuallyHidden
          strict
          as='button'
          isVisible={isImageNextable}
          className={clsx(style.Arrow, style.ArrowNext)}
          onClick={nextImage}
          aria-label='Next image'
          aria-disabled={!isImageNextable}
        >
          <img src={arrowRightSvg} alt='' />
        </VisuallyHidden>
      </div>
    </div>
  )
})
